import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { Alert, AlertDescription, AlertTitle, Badge, Button, CopyableId, EntityDrawer, Input, Label, Skeleton, Switch } from "@fox1ck-org/ui";
import { PortField } from "@fox1ck-org/ui/saas";
import { toast } from "@fox1ck-org/ui/toast";
import { useEntitlements, usePorts, useSaas } from "@fox1ck-org/auth/saas/react";
import { portRefOf } from "@fox1ck-org/auth/ui";
import type { BindingRecord, TrackingDomain } from "@gen/tracker/v1/tracking_domain_pb";

import { PRODUCT } from "@/lib/auth";
import { isProviderUnavailable } from "@/lib/domain-binding";
import { domainFieldState, domainPortRefusal, domainsPortOf, recordHostFor } from "@/lib/domain-port";
import { getLocale, useT } from "@/lib/i18n";
import { showRefusal } from "@/lib/plan";
import { useCreateTrackingDomain, useDomainPort } from "@/lib/services/tracking-domain-service";

/**
 * «Подключить домен» — the tracker's `domains` port (domains.attach.v1)
 * inside the flow, drawn by `PortField`:
 *
 * - a module closes the port → one click: the caller's free domains at the
 *   module, or any name (the module writes the record itself), and the way
 *   to get a new one there;
 * - manual → the address, the record to set at the DNS host, how it is
 *   checked, and honestly when clicks will reach it; «Или добавьте модуль …»
 *   (ecosystem without the module) or «подключите источник» is the other way;
 * - the tracker cannot use the port (module not answering, not wired, a
 *   connector before it exists) → who, «Повторить».
 *
 * What closes the port is the org's Connection in console — nothing here
 * decides it or names a module: the words come from console and the adapter.
 */
export function ConnectDomainDrawer({ open, onOpenChange, onCreated }: { open: boolean; onOpenChange: (open: boolean) => void; onCreated: (d: TrackingDomain) => void }) {
  const t = useT();
  const locale = getLocale();
  const navigate = useNavigate();
  const { links } = useSaas();
  const ent = useEntitlements();
  const ports = usePorts(PRODUCT);
  const answer = useDomainPort(open);
  const create = useCreateTrackingDomain();
  const [fqdn, setFqdn] = useState("");
  const [isDefault, setIsDefault] = useState(false);

  const port = useMemo(() => domainsPortOf(ports.ports.map((p) => portRefOf(p))), [ports.ports]);
  const canEdit = !ent.isLoading && (ent.orgRole === "owner" || ent.orgRole === "admin");
  const dp = answer.data;
  const state = domainFieldState({ kind: dp?.kind, port, canEdit, failed: answer.isError });
  const native = dp?.kind === "native" && !answer.isError;
  // Console's name for what closes the port; the adapter's own until console answers.
  const adapter = port?.adapter?.kind === "native" || port?.adapter?.kind === "connector" ? port.adapter.title : (dp?.label ?? "");
  const module = port?.offer?.[0];

  const attach = (name: string) => {
    const v = name.trim();
    if (!v) return;
    create.mutate(
      { fqdn: v, isDefault },
      {
        onSuccess: (d) => {
          toast.success(t(native ? "domains.port.native.done" : "domains.create.done", { fqdn: d.fqdn }));
          setFqdn("");
          setIsDefault(false);
          onCreated(d);
        },
        onError: (e) => void (showRefusal(e) || toast.error(failureText(t, e))),
      },
    );
  };

  const connect = () => {
    if (module) window.open(links.connect(module.id), "_blank", "noopener");
    else navigate("/connections");
  };

  const input = (
    <div className="flex flex-col gap-1.5">
      <Label textCase="none" htmlFor="fqdn">
        {native ? t("domains.port.other") : t("domains.create.fqdn")}
      </Label>
      <Input id="fqdn" autoFocus placeholder={t("domains.create.fqdn.placeholder")} value={fqdn} onChange={(e) => setFqdn(e.target.value)} onKeyDown={(e) => e.key === "Enter" && attach(fqdn)} />
      <span className="text-xs text-muted-foreground">{t("domains.create.fqdn.hint")}</span>
    </div>
  );

  const manual = (
    <div className="flex flex-col gap-4">
      {input}
      <section className="flex flex-col gap-2" aria-label={t("domains.port.manual.records")}>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("domains.port.manual.records")}</h3>
        <Records records={dp?.records ?? []} fqdn={fqdn} t={t} />
        <p className="m-0 text-xs text-muted-foreground">{t("domains.port.manual.steps")}</p>
      </section>
      <Alert variant="info">
        <div className="min-w-0">
          <AlertTitle>{t("domains.port.manual.limit.title")}</AlertTitle>
          <AlertDescription>{t("domains.port.manual.limit.body")}</AlertDescription>
        </div>
      </Alert>
    </div>
  );

  let body;
  if (answer.isPending || (ports.isLoading && !answer.isError)) {
    body = <Skeleton className="h-40 w-full" />;
  } else {
    body = (
      <div className="flex flex-col gap-4">
        <PortField
          label={t("domains.port.label")}
          port={port}
          state={state}
          canEdit={canEdit}
          failed={answer.isError}
          locale={locale}
          summary={native ? <b>{t("domains.port.native.summary")}</b> : undefined}
          detail={native && dp?.extended.includes("register") ? t("domains.port.native.register", { adapter }) : undefined}
          labels={answer.isError ? { noAnswer: () => portFailure(t, answer.error, adapter) } : undefined}
          onConnect={canEdit ? connect : undefined}
          onRetry={() => void answer.refetch()}
        >
          {state === "no-answer" ? undefined : manual}
        </PortField>
        {native ? (
          <>
            <FreeDomains t={t} adapter={adapter} free={dp?.freeDomains ?? []} pending={create.isPending} onTake={attach} />
            {input}
            {dp?.domainsUrl ? (
              <Button asChild variant="ghost" size="sm" className="self-start">
                <a href={dp.domainsUrl} target="_blank" rel="noreferrer">
                  {t("domains.port.buy", { adapter })} <ExternalLink className="size-3.5" />
                </a>
              </Button>
            ) : null}
          </>
        ) : null}
        {state === "no-answer" && canEdit && !port?.locked ? (
          <Button variant="ghost" size="sm" className="self-start" onClick={() => navigate("/connections")}>
            {t("domains.port.failed.change")}
          </Button>
        ) : null}
        {state !== "no-answer" ? (
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={isDefault} onCheckedChange={setIsDefault} />
            {t("domains.create.default")}
          </label>
        ) : null}
      </div>
    );
  }

  return (
    <EntityDrawer
      open={open}
      onOpenChange={onOpenChange}
      width="lg"
      title={t("domains.create.title")}
      description={t("domains.create.description")}
      primaryAction={{ label: t("domains.create.submit"), onClick: () => attach(fqdn), disabled: !fqdn.trim() || state === "no-answer", loading: create.isPending }}
      secondaryAction={{ label: t("ops.cancel"), onClick: () => onOpenChange(false) }}
    >
      {body}
    </EntityDrawer>
  );
}

type T = ReturnType<typeof useT>;

/** A Connect failure the buyer can act on, or the screen's own sentence. */
function failureText(t: T, err: unknown): string {
  if (isProviderUnavailable(err)) return t("domains.unavailable.body");
  const fallback = t("domains.create.failed");
  return err instanceof Error && err.message ? `${fallback}: ${err.message}` : fallback;
}

/** Why the port does not answer, naming who — never «офлайн». */
function portFailure(t: T, err: unknown, adapter: string): string {
  const reason = domainPortRefusal(err);
  if (reason === "adapter_not_wired") return t("domains.port.failed.notWired", { adapter: adapter || t("domains.port.label") });
  if (reason === "connector_unavailable") return t("domains.port.failed.connector");
  return t("domains.port.failed.noAnswer", { adapter: adapter || t("domains.port.label") });
}

/** The caller's free domains at the module: one click attaches one. */
function FreeDomains({ t, adapter, free, pending, onTake }: { t: T; adapter: string; free: string[]; pending: boolean; onTake: (fqdn: string) => void }) {
  return (
    <section className="flex flex-col gap-2" aria-label={t("domains.port.free.title", { adapter })}>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("domains.port.free.title", { adapter })}</h3>
      {free.length === 0 ? (
        <p className="m-0 text-xs text-muted-foreground">{t("domains.port.free.none", { adapter })}</p>
      ) : (
        <div className="max-h-64 overflow-y-auto rounded-md border border-border">
          {free.map((d) => (
            <div key={d} className="flex items-center gap-3 border-b border-border px-3 py-2 last:border-b-0">
              <span className="min-w-0 flex-1 truncate font-mono text-sm">{d}</span>
              <Button variant="secondary" size="sm" disabled={pending} onClick={() => onTake(d)}>
                {t("domains.port.free.take")}
              </Button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/** The records a manual domain must carry, on the name the person typed. */
function Records({ records, fqdn, t }: { records: BindingRecord[]; fqdn: string; t: T }) {
  return (
    <div className="overflow-hidden rounded-md border border-border">
      {records.map((r, i) => (
        <div key={`${r.type}-${r.name}-${i}`} className="grid grid-cols-[--spacing(16)_minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-3 border-b border-border px-3 py-2 text-sm last:border-b-0">
          <span className="font-mono text-xs">{r.type}</span>
          <span className="truncate font-mono text-xs text-muted-foreground">{recordHostFor(fqdn, r.name)}</span>
          <CopyableId value={r.content} className="min-w-0 font-mono text-xs" />
          <Badge variant="outline">{r.proxied ? t("ops.dom.proxied") : t("ops.dom.dnsOnly")}</Badge>
        </div>
      ))}
    </div>
  );
}
